/**
 * @component UAlert
 *
 * @description
 * The UAlert component provides feedback messages for typical user actions. It comes with three
 * predefined types: success, error, and warning. Each type has its own icon, background, and text color.
 *
 * @props
 * - `type` (default: "success"): Determines the type of the alert (e.g., 'success', 'error', 'warning').
 * - `message`: The content or message to display in the alert.
 *
 * @slots
 * - None
 *
 * @events
 * - None
 *
 * @example
 * ```vue
 * <UAlert type="error" message="Something went wrong!" />
 * ```

 */
<template>
  <div
    :class="alertType.backgroundColor"
    class="p-4 rounded-md"
    role="alert"
  >
    <div class="flex items-center">
      <!-- Icon -->
      <div class="flex-shrink-0">
        <u-icons
          :icon-type="alertType.icon"
          :class="alertType.iconColor"
          class="w-6 h-6"
        />
      </div>

      <!-- Message -->
      <div class="ml-2">
        <div
          :class="alertType.textColor"
          class="text-sm font-medium leading-5"
        >
          {{ message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import UIcons from '../Icons/Icons.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'success',
    validator: (value) => ['success', 'error', 'warning'].includes(value)
  },
  message: {
    type: String,
    default: ''
  }
});

const types = [
  { name: 'success', icon: 'check', iconColor: 'text-success', backgroundColor: 'bg-success-light', textColor: 'text-success-dark' },
  { name: 'error', icon: 'closeRound', iconColor: 'text-error', backgroundColor: 'bg-error-light', textColor: 'text-error-dark' },
  { name: 'warning', icon: 'warning', iconColor: 'text-warning', backgroundColor: 'bg-warning-light', textColor: 'text-warning-dark' },
];

const alertType = computed(() => types.find(i => i.name === props.type));

</script>
